import { createReducer, on } from '@ngrx/store';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { AuditEvent } from '../../../../../shared/src/lib/types/r4b.type';
import * as AuditLogActions from '../actions/audit.log.actions';

/**
 * Audit Log State
 */
export interface AuditLogState {
    entities: { [id: number]: AuditEvent };
    backendPages: { [pageIndex: number]: string[] };
    backendPageSize: number;
    totalCount: number;
    totalCountExact: boolean;
    pageSize: number;
    currentPage: number;
    paginationId: string;
    loading: boolean;
    error: any | null;
}

/**
 * initial Audit Log State
 */
export const initialState: AuditLogState = {
    entities: {},
    backendPages: {},
    backendPageSize: 50,
    totalCount: 0,
    totalCountExact: false,
    pageSize: 10,
    currentPage: 0,
    paginationId: null,
    loading: false,
    error: null,
};

/**
 * Audit Log reducer
 */
export const auditLogReducer = createReducer(
    initialState,
    on(AuditLogActions.loadAuditLogs, (state, { backendPage }) => {
        // If the page is already cached, return current state unchanged
        if (state.backendPages[backendPage]) {
            return state;
        }

        return {
            ...state,
            loading: true,
            error: null,
        };
    }),
    on(
        AuditLogActions.loadAuditLogsSuccess,
        (state, { backendPage, items, totalCount, hasNext, paginationId, backendPageSize }) => {
            // Normalize Data: Convert array to object
            const newEntities = { ...state.entities };
            const newItemIds = items.map(item => {
                newEntities[item.id] = item;
                return item.id;
            });

            // Bundle.total is optional in FHIR: without it, the length is the lower bound "loaded so far (+1 if a next page exists)"
            const loadedEnd = backendPage * backendPageSize + items.length + (hasNext ? 1 : 0);
            const exactTotal = totalCount ?? (state.totalCountExact ? state.totalCount : hasNext ? null : loadedEnd);

            return {
                ...state,
                entities: newEntities,
                backendPages: { ...state.backendPages, [backendPage]: newItemIds },
                backendPageSize,
                totalCount: exactTotal ?? Math.max(state.totalCount, loadedEnd),
                totalCountExact: exactTotal != null,
                paginationId,
                loading: false,
            };
        },
    ),
    on(AuditLogActions.loadAuditLogsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message || 'An unknown error occurred',
    })),
    on(AuditLogActions.loadAuditLogsCancelled, state => ({
        ...state,
        loading: false,
        error: null,
    })),
    on(AuditLogActions.setPage, (state, { currentPage, pageSize }) => ({
        ...state,
        currentPage,
        pageSize,
    })),
    on(AuditLogActions.clearAuditLog, () => initialState),
);
